import { useCallback, useEffect } from 'react';
import {
  matchPath,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { MapRef, useMapInstance } from 'shared/map-exports';
import { LatLng } from 'shared/types';
import usePrevious from 'hooks/usePrevious';
import { getMapZoomInteger } from 'components/Map/utils';
import { useMapZoomInteger } from 'hooks/useMapZoomInteger';
import { useAuthState } from 'state/useAuthState';
import {
  COORDINATE_DISPLAY_FRACTION_DIGITS,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
} from '../../constants';
import { useMapURLParams } from './useMapURLParams';

type UseSetMapPin = {
  setSelectedMapPin: (coords: LatLng) => void;
};

const splitUrlIntoPathAndCoords = (
  url: string,
): { pathName: string; coordsString: string } => {
  const [pathName, coordsString = ''] = url.split('@');
  return { pathName, coordsString };
};

const buildCoordinatesUrlParam = (
  pathName: string,
  coords: LatLng,
  map?: MapRef,
): string => {
  const lat = coords.lat.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS);
  const lng = coords.lng.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS);
  const zoom = getMapZoomInteger(map);
  if (zoom) {
    return `${pathName}@${lat},${lng},${zoom}z`;
  }
  return `${pathName}@${lat},${lng}`;
};

const adjustZoomLevel = (locationPathname: string, newZoom: number): string => {
  const { pathName, coordsString } =
    splitUrlIntoPathAndCoords(locationPathname);
  const baseCoords = coordsString.replace(/,\d+z?$/, '');
  return `${pathName}@${baseCoords},${newZoom}z`;
};

// Syncs URL-based map pin selection with map zoom level.
export const useSyncMapPin = (): void => {
  const history = useHistory();
  const { latitudeParam, longitudeParam } = useMapURLParams();
  const routeMatch = useRouteMatch({ path: ['/@:lat,:lng', '*/@:lat,:lng'] });
  const currentLocationUrl = routeMatch?.url;
  const mapZoom = useMapZoomInteger();
  const prevMapZoom = usePrevious(mapZoom);

  // Sync URL with the map's zoom level if the URL has lat/lng params
  useEffect(() => {
    if (
      latitudeParam &&
      longitudeParam &&
      prevMapZoom !== mapZoom &&
      mapZoom &&
      currentLocationUrl
    ) {
      const updatedPathname = adjustZoomLevel(currentLocationUrl, mapZoom);
      history.replace(updatedPathname);
    }
  }, [
    history,
    currentLocationUrl,
    mapZoom,
    prevMapZoom,
    latitudeParam,
    longitudeParam,
  ]);
};

/**
 * A custom hook for selecting a map pin.
 * `setSelectedMapPin` updates the browser's URL params to include:
 * - The selected map pin's coordinates
 * - The current zoom level (if available)
 */
export const useSetMapPin = (): UseSetMapPin => {
  const history = useHistory();
  const map = useMapInstance();
  const location = useLocation();

  const setSelectedMapPin = useCallback(
    (coords: LatLng): void => {
      let locationPathname = '/';
      if (
        matchPath(location.pathname, {
          path: WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
        })
      ) {
        // Make sure pathname doesn't already include lat, lng and zoom params
        locationPathname = `${location.pathname.split('/@')[0]}/`;
      }
      const coordinatesPath = buildCoordinatesUrlParam(
        locationPathname,
        coords,
        map,
      );
      history.push(coordinatesPath);
    },
    [history, location.pathname, map],
  );

  return { setSelectedMapPin };
};

export const useMapPinEnabled = (): boolean => {
  const {
    showMembershipProFeatures,
    permissions: { isInternalUser },
  } = useAuthState();
  return showMembershipProFeatures || isInternalUser;
};
